// components/NewsRow.js
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Menu,
  MenuItem,
  useMediaQuery,
  Divider,
  Avatar,
  useEventCallback,
} from '@mui/material';
import {
  ThumbUpSharp,
  Delete,
  Edit,
  InsertComment,
  Visibility,
  StarBorder,
  StarOutlined,
  MoreVert,
  Title,
  ThumbUpAltOutlined,
  InsertCommentOutlined,
  VisibilityOutlined,
  Public,
  ThumbUpOutlined,
} from '@mui/icons-material';
import UserAvatar from '../../../common/UserAvatar';
import { useSelector } from 'react-redux';
import { selectCurrentUserEmpCode } from '../../Auth/authSlice';
import { ItemLoader } from './GnnSkeleton';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../context/Ability';
import defaultimg from './default.png';
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter';
import { formatDateWithSuffix } from '../../../utils/dateUtils';
import { Button } from 'react-scroll';
import { useTheme } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { useLikeNewsMutation, useMarkAsTop5NewsMutation, useUnlikeNewsMutation } from '../gnnApiSlice';
import { useExpanded } from '../contexts/ExpandedContext';
import { LazyLoadComponent, LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css'; // Optional: add a blur effect for lazy-loaded images
import AttachAvatar from '../../../common/AttachAvatar';
import Carousel from 'react-material-ui-carousel';

const NewsDescription = ({ description, id, setSize, index }) => {
  const { expandedItems, toggleExpanded } = useExpanded();
  const MAX_LENGTH = 100; // Maximum number of characters to display before "Show More"
  const descriptionRef = useRef(null);
  const [truncatedDescription, setTruncatedDescription] = useState('');
  const [isTruncated, setIsTruncated] = useState(false);
  const [showButton, setShowButton] = useState(false);

  useLayoutEffect(() => {
    if (description) {
      if (description?.length > MAX_LENGTH) {
        const truncatedText = description.substring(0, MAX_LENGTH) + '...';
        setTruncatedDescription(truncatedText);
        setIsTruncated(true);
        setShowButton(true);
      } else {
        setTruncatedDescription(description);
        setIsTruncated(false);
      }
      // if (descriptionRef.current && index) {
      //   setSize(index, descriptionRef.current.getBoundingClientRect().height);
      // }
    }
  }, [description]);

  return (
    <Typography paragraph sx={{ fontSize: '12px', overflow: 'hidden' }} ref={descriptionRef}>
      <span
        style={{ whiteSpace: 'no-wrap' }}
        dangerouslySetInnerHTML={{ __html: expandedItems[id] || !isTruncated ? description : truncatedDescription }}
      />

      {showButton && (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggleExpanded(id);
          }}
          variant="text"
          size="small"
          style={{
            background: 'none',
            display: 'inline',
            textTransform: 'none',
            padding: 0,
            minWidth: 'auto',
            border: 'none',
            color: 'grey',
          }}
        >
          ...{expandedItems[id] ? 'collapse' : 'see more'}
        </Button>
      )}
    </Typography>
  );
};
const NewsRow = React.memo(
  ({ news, handleCardClick, handleDeleteClick, handleLikesClick, handleEditClick, onMediaLoad }) => {
    const mediaFiletypes = /mp4|mkv|webm|mov|avi|wmv|flv|3gp|mp3/;
    const [showMore, setShowMore] = useState(false);
    const empCode = useSelector(selectCurrentUserEmpCode);
    const ability = useAbility(AbilityContext);
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down('sm'));
    const [likeNews] = useLikeNewsMutation();
    const [unlikeNews] = useUnlikeNewsMutation();
    const [markAsTop5News] = useMarkAsTop5NewsMutation();
    const rowRef = useRef();
    const truncatedRef = useRef();
    const { expandedItems, toggleExpanded } = useExpanded();
    const MAX_LENGTH = 100; // Maximum number of characters to display before "Show More"
    const descriptionRef = useRef(null);
    const [truncatedDescription, setTruncatedDescription] = useState('');
    const [isTruncated, setIsTruncated] = useState(false);
    const [showButton, setShowButton] = useState(false);
    const isVideo = useCallback((src) => {
      return mediaFiletypes.test(src?.split('.').pop());
    }, []);
    const navigate = useNavigate();
    const handleMenuOpen = (event) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (event) => {
      event.stopPropagation();
      setAnchorEl(null);
    };
    const handleMarkAsTop5Click = async (event, id) => {
      event.stopPropagation();
      try {
        await markAsTop5News({ id });
      } catch (error) {
        console.error('Error marking news as top 5:', error);
      }
    };

    const handleLikeClick = useCallback(
      async (event, id) => {
        event.stopPropagation();
        event.preventDefault(); // Prevent selection
        try {
          await likeNews({ id, empCodeData: { empCode } });
        } catch (error) {
          console.error('Error liking news:', error);
        }
      },
      [likeNews, empCode]
    );
    const handleUnlikeClick = useCallback(
      async (event, id) => {
        event.stopPropagation();
        event.preventDefault(); // Prevent selection

        try {
          await unlikeNews({ id, empCodeData: { empCode } });
        } catch (error) {
          console.error('Error unliking news:', error);
        }
      },
      [unlikeNews, empCode]
    );

    const avatarSrc = useMemo(() => {
      // console.log('called', news?.attachments);
      return news?.attachments[0] ? `${process.env.REACT_APP_PUBLIC_UPLOADS_URL}${news?.attachments[0]}` : defaultimg;
    }, [news?.attachments[0]]);

    // const [avatarSrc, setAvatarSrc] = useState(null);
    // useEffect(() => {
    //   if (news?.attachments[0]) {
    //     setAvatarSrc(`${process.env.REACT_APP_PUBLIC_UPLOADS_URL}${news?.attachments[0]}`);
    //   } else {
    //     setAvatarSrc(defaultimg);
    //   }
    // }, [news?.attachments]);
    const isNewsLiked = useCallback((news) => news?.likes?.some((like) => like?.empCode === empCode), [empCode]);

    // useLayoutEffect(() => {
    //   if (descriptionRef.current) {
    //     updateItemSize(descriptionRef.current.scrollHeight + 200); // Example base height plus content
    //   }
    // }, [news.description, updateItemSize]);

    useEffect(() => {
      if (news?.description) {
        if (news?.description?.length > MAX_LENGTH) {
          const truncatedText = news?.description.substring(0, MAX_LENGTH) + '...';
          setTruncatedDescription(truncatedText);
          setIsTruncated(true);
          setShowButton(true);
        } else {
          setTruncatedDescription(news?.description);
          setIsTruncated(false);
        }
        // if (descriptionRef.current && index) {
        //   setSize(index, descriptionRef.current.getBoundingClientRect().height);
        // }
      }
      // if (rowRef.current && descriptionRef.current && truncatedRef.current) {
      //   const fullHeight = rowRef.current.getBoundingClientRect().height;
      //   const descriptionHeight = descriptionRef.current.getBoundingClientRect().height;
      //   const truncatedHeight = truncatedRef.current.clientHeight;
      //   console.log(descriptionHeight, 'size', fullHeight, truncatedHeight);

      //   const collapsedHeight =
      //     fullHeight -
      //     descriptionHeight +
      //     (expandedItems[news?._id] ? descriptionHeight : Math.min(descriptionHeight, truncatedHeight)); // Adjusted for collapsed view

      //   // setSize(index, expandedItems[news?._id] ? fullHeight : collapsedHeight);
      // }
    }, [news?.description]);

    return (
      <div ref={rowRef} style={{}}>
        <Card
          className={`news-row-${news?._id}`}
          sx={{
            marginBottom: '0.75rem',
            // boxShadow: 'rgba(0, 0, 0, 0.15) 2.4px 2.4px 3.2px',
            background: 'white',
            boxShadow: 'none',
            borderTop: 1,
            borderBottom: 1,
            borderColor: 'divider',
            borderRadius: 0,
            width: '100%',
            height: '100%',
            // width: isSm ? '100vw' : '30vw',
            display: 'flex',
            flexDirection: 'column',
            // cursor: 'default',
            ':focus': {
              outline: 'none',
              boxShadow: 'none',
              backgroundColor: 'white',
            },
            cursor: 'default', // Set to 'default' if you want to indicate it's not clickable

            userSelect: 'none',
            // '&:hover': {
            //   boxShadow: 'rgba(136, 165, 191, 0.48) 6px 2px 16px 0px, rgba(255, 255, 255, 0.8) -6px -2px 16px 0px',
            // },
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick(news?._id);
          }}
        >
          <CardContent
            sx={{
              flexGrow: 1,
              padding: 0,
              ':focus': {
                outline: 'none',
                boxShadow: 'none',
                backgroundColor: 'white',
              },
              userSelect: 'none',
            }}
          >
            <Grid sx={{ display: 'flex', mt: 1, mb: 2, userSelect: 'none' }}>
              <Avatar
                // showStatus={false}
                sx={{
                  width: 35,
                  height: 35,
                  mr: 1,
                 img: { objectFit: 'cover', objectPosition: 'top' }
                }}
                src={news?.authorUserPhoto}
                alt={news?.authorName}
              />
              <Grid sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Grid sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', float: 'right' }}>
                  <Typography sx={{ color: 'black', fontSize: '13px', fontWeight: 'bold' }}>
                    {news?.authorName.trim() || 'Gateway Group Of Companies'}
                  </Typography>
                  <Box sx={{ display: 'flex' }}>
                    {news?.categories != 'Internal Posts' ? (
                      <>
                        {ability.can('update', 'Gnn') ||
                        ability.can('manage', 'Gnn') ||
                        ability.can('read', 'Admin') ? (
                          <>
                            {news?.top5News ? (
                              <Tooltip title="Remove from Top 5 News">
                                <IconButton
                                  size="small"
                                  onClick={(event) => handleMarkAsTop5Click(event, news?._id)}
                                  sx={{ padding: 0, mr: 1 }}
                                >
                                  <StarOutlined sx={{ fontSize: '17px', fontWeight: '500' }} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Add to Top 5 News">
                                <IconButton
                                  size="small"
                                  onClick={(event) => handleMarkAsTop5Click(event, news?._id)}
                                  sx={{ padding: 0, mr: 1 }}
                                >
                                  <StarBorder sx={{ fontSize: '17px', fontWeight: '500' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        ) : null}
                      </>
                    ) : (
                      <></>
                    )}
                    {(news?.empCode === empCode || ability.can('manage', 'Gnn') || ability.can('read', 'Admin')) && (
                      <Grid container justifyContent="flex-end" alignItems="center">
                        <Tooltip title="Options">
                          <IconButton size="small" onClick={handleMenuOpen} sx={{ padding: 0.2 }}>
                            <MoreVert sx={{ fontSize: '15px', fontWeight: '500', color: 'grey', mr: 1 }} />
                          </IconButton>
                        </Tooltip>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                          <MenuItem
                            onClick={(event) => {
                              handleMenuClose(event);
                              handleEditClick(event, news);
                            }}
                          >
                            <Edit sx={{ fontSize: '15px', fontWeight: '500', color: 'grey', mr: 1 }} />{' '}
                            <Typography sx={{ fontSize: '14px' }}>Edit</Typography>
                          </MenuItem>
                          <MenuItem onClick={(event) => handleDeleteClick(event, news?._id)}>
                            <Delete sx={{ fontSize: '15px', fontWeight: '500', color: 'grey', mr: 1 }} />{' '}
                            <Typography sx={{ fontSize: '14px' }}>Delete</Typography>
                          </MenuItem>
                        </Menu>
                      </Grid>
                    )}
                  </Box>
                </Grid>
                <Grid sx={{ display: 'flex', flexDirection: 'row' }}>
                  <Typography sx={{ color: 'grey', fontSize: '11px', lineHeight: '20px' }}>
                    {formatDateWithSuffix(new Date(news?.createdAt).toLocaleDateString('en-GB'))}
                  </Typography>{' '}
                  <Typography sx={{ color: 'grey', fontSize: '11px', ml: 0.4, mr: 0.4, lineHeight: '20px' }}>
                    •
                  </Typography>{' '}
                  {news?.categories == 'Public news' ? (
                    <>
                      <Public
                        sx={{
                          fontWeight: 'bold',
                          color: 'grey',
                          fontSize: '11px',
                          mt: 0.6,
                          lineHeight: '20px',
                          mr: 0.4,
                        }}
                      />{' '}
                    </>
                  ) : (
                    <></>
                  )}
                  <Typography variant="body2" color="grey" sx={{ color: 'grey', fontSize: '12px', lineHeight: '20px' }}>
                    {news?.categories.join(', ')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* <Typography sx={{ fontSize: '12px', mb: 0.3 }}>{capitalizeFirstLetter(news?.title)}</Typography> */}
            <div style={{}}>
              {/* <NewsDescription description={news?.description} showMore={showMore} onToggleShowMore={handleToggleShowMore} /> */}
              {/* <NewsDescription
                description={news?.description}
                // showMore={isExpanded}
                // onToggleShowMore={toggleExpanded}
                // descriptionRef={descriptionRef}
                id={news?._id}
                setSize={setSize}
                index={index}
                // onResize={(height) => updateItemSize(height + 100)} // Adjust height dynamically
              /> */}
              <div ref={descriptionRef}>
                <Typography paragraph sx={{ fontSize: '12px', overflow: 'hidden' }}>
                  <span
                    style={{ whiteSpace: 'no-wrap' }}
                    dangerouslySetInnerHTML={{
                      __html: expandedItems[news?._id] || !isTruncated ? news?.description : truncatedDescription,
                    }}
                  />
                </Typography>
              </div>
              <div ref={truncatedRef} style={{ position: 'absolute', visibility: 'hidden' }}>
                <Typography paragraph sx={{ fontSize: '12px' }}>
                  <span
                    style={{ whiteSpace: 'no-wrap' }}
                    dangerouslySetInnerHTML={{
                      __html: truncatedDescription,
                    }}
                  />
                </Typography>
              </div>

              {showButton && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleExpanded(news?._id);
                  }}
                  variant="text"
                  size="small"
                  style={{
                    background: 'none',
                    display: 'inline',
                    textTransform: 'none',
                    padding: 0,
                    minWidth: 'auto',
                    border: 'none',
                    color: 'grey',
                  }}
                >
                  ...{expandedItems[news?._id] ? 'collapse' : 'see more'}
                </Button>
              )}
            </div>

            <>
              {news?.categories != 'Internal Posts' ? (
                <></>
              ) : (
                // <Grid sx={{ display: 'flex', mt: 1 }}>
                //   <Typography variant="body2" sx={{ color: 'grey' }}>
                //     Posted by {news?.authorName}
                //   </Typography>
                //   <UserAvatar sx={{ width: 19, height: 19, mr: 1, ml:1 }} src={news?.authorUserPhoto} alt="Avatar" />
                // </Grid>
                <></>
              )}
            </>
          </CardContent>
          {/* // <CardMedia
          //   component={mediaFiletypes.test(news?.attachments[0]?.split('.').pop()) ? 'video' : 'div'}
          //   alt="News"
          //   sx={{ objectFit: 'cover', padding: 0, height: '18vh' }}
          //   controls={mediaFiletypes.test(news?.attachments[0]?.split('.').pop()) ? true : undefined}
          //   onClick={() => handleCardClick(news?._id)}
          // >
          //   <LazyLoadImage
          //     src={avatarSrc}
          //     alt="News"
          //     effect="throttle"
          //     style={{
            //       height: '18vh',
            //       objectFit: 'cover',
            //       width: '100vw',
            //     }}
          //     wrapperClassName="avatar-wrapper"
          //     threshold={2000}
          //     visibleByDefault={false}
          //   />
          // </CardMedia> */}

          {/* // {news?.attachments[0] && (
          
        //   <AttachAvatar
        //     src={news?.attachments[0]}
        //     controls
        //     autoPlay={false}
        //     // muted={true}
        //     variant='rounded'
        //     className='attachment-avatar'
        //     sx={{
          //       height: '18vh',
        //       objectFit: 'cover',
        //       width: '100vw',
        //     }}
        //   />
        // )} */}

          <>
            {
              news?.attachments.length === 1 && (
                <AttachAvatar
                  src={news?.attachments[0] || defaultimg}
                  autoPlay={true}
                  muted={true}
                  variant="rounded"
                  className="attachment-avatar"
                  sx={{
                    minHeight: '40vh',
                    height: '40vh',
                    objectFit: 'contain',
                    width: '100%',
                  }}
                  onError={(e) => {
                    e.target.onerror = null; // prevent infinite loop if defaultimg also fails
                    e.target.src = defaultimg;
                  }}
                  // onLoad={() => onMediaLoad()}
                />
              )
              // <CardMedia
              //   component={mediaFiletypes.test(news?.attachments[0].split('.').pop()) ? 'video' : 'img'}
              //   src={`${process.env.REACT_APP_PUBLIC_UPLOADS_URL}${news?.attachments[0]}`}
              //   image={!news?.attachments[0] ? defaultimg : undefined}
              //   alt='News'
              //   loading='lazy'
              //   sx={{ height: '40vh', objectFit: 'contain', width: '100%' }}
              //   controls={mediaFiletypes.test(news?.attachments[0].split('.').pop())}
              // />
            }

            {news?.attachments.length > 1 && (
              <Carousel
                autoPlay={false}
                indicators
                navButtonsAlwaysInvisible // Hide next and previous buttons
                sx={{
                  height: '40vh',
                  width: '100%',
                }}
                // Add a custom component to display the current image index
                renderIndicator={({ index, onClick }) => (
                  <Box
                    sx={{
                      zIndex: 1000,
                      // position: 'absolute',
                      bottom: 0,
                      right: 0,
                      background: 'red',
                      color: 'black',
                      padding: '5px 10px',
                      borderRadius: '3px',
                    }}
                  >
                    {index + 1}/{news.attachments.length} media
                  </Box>
                )}
              >
                {news?.attachments.map((attachment, index) => (
                  <>
                    {/* <Box key={index} sx={{ height: '40vh', width: '100%' }}>
                      <AttachAvatar
                        key={index}
                        src={attachment}
                        variant="rounded"
                        className="attachment-avatar"
                        sx={{
                          height: 'auto',
                          objectFit: 'contain',
                          width: 'auto',
                        }}
                        autoPlay={false}
                      />
                      <Box
                        sx={{
                          zIndex: 1000,
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          background: 'rgb(0,0,0,0.7)',
                          color: 'white',
                          padding: '5px 10px',
                          borderRadius: '3px',
                        }}
                      >
                        <Typography sx={{ fontSize: '12px', color: 'white' }}>
                          {index + 1}/{news.attachments.length} Media
                        </Typography>
                      </Box>
                    </Box> */}
                    <Box
                      key={index}
                      sx={{
                        height: '40vh',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      {/* <CardMedia
                      component={mediaFiletypes.test(attachment.split('.').pop()) ? 'video' : 'img'}
                      src={`${process.env.REACT_APP_PUBLIC_UPLOADS_URL}${attachment}`}
                      image={!attachment ? defaultimg : undefined}
                      alt="News"
                      loading="lazy"
                      sx={{ height: '40vh', objectFit: 'contain', width: '100%' }}
                      controls={mediaFiletypes.test(attachment.split('.').pop())}
                    /> */}
                      <AttachAvatar
                        src={attachment}
                        autoPlay={true}
                        muted={true}
                        alt="News"
                        variant="rounded"
                        className="attachment-avatar"
                        sx={{
                          height: '40vh',
                          objectFit: 'contain',
                          width: '100%',
                        }}
                        onError={(e) => {
                          e.target.onerror = null; // prevent infinite loop if defaultimg also fails
                          e.target.src = defaultimg;
                        }}
                      />
                      <Box
                        sx={{
                          zIndex: 1000,
                          position: 'absolute',
                          bottom: 0,
                          right: '1%',
                          background: 'rgb(0,0,0,0.7)',
                          color: 'white',
                          padding: '5px 10px',
                          borderRadius: '3px',
                        }}
                      >
                        <Typography sx={{ fontSize: '12px', color: 'white' }}>
                          {index + 1}/{news.attachments.length} Media
                        </Typography>
                      </Box>
                    </Box>
                  </>
                ))}
              </Carousel>
            )}
          </>

          <Grid
            container
            sx={{
              display: 'flex',
              flexDirection: 'row',
              mt: 2,
              textAlign: 'center',
              width: '100%',
              justifyContent: 'space-between',
              userSelect: 'none',
            }}
          >
            <Grid
              item
              sx={{ display: 'flex', flexDirection: 'row' }}
              onClick={(event) => handleLikesClick(event, news?.likes)}
            >
              <ThumbUpOutlined sx={{ fontSize: '15px', color: 'grey' }} />
              <Typography
                sx={{
                  ml: 0.7,
                  cursor: 'default',
                  color: 'grey',
                  fontWeight: '500',
                  fontSize: '12px',
                  userSelect: 'none',
                }}
              >
                {news?.likes.length || 0} likes
              </Typography>
            </Grid>

            <Grid item sx={{ display: 'flex', flexDirection: 'row', float: 'right' }}>
              <Grid sx={{ display: 'flex', flexDirection: 'row', ml: 1 }}>
                <Tooltip title="Views">
                  <VisibilityOutlined sx={{ color: 'grey', fontSize: '18px' }} />
                </Tooltip>
                <Typography
                  sx={{
                    ml: 0.7,
                    cursor: 'default',
                    color: 'grey',
                    fontWeight: '500',
                    fontSize: '12px',
                  }}
                >
                  {news?.viewCount.length || 0} views
                </Typography>
              </Grid>

              <Grid sx={{ display: 'flex', flexDirection: 'row', ml: 1 }}>
                <IconButton sx={{ padding: 0 }} onClick={() => navigate(`/home/announcement/gnn/`)}>
                  <Tooltip title="Comments">
                    <InsertCommentOutlined sx={{ color: 'grey', fontSize: '15px' }} />
                  </Tooltip>
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{
                    ml: 0.7,
                    cursor: 'default',
                    color: 'grey',
                    fontWeight: '500',
                    fontSize: '12px',
                  }}
                >
                  {news?.comments.length || 0} comments
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Box
            sx={{
              display: 'flex',
              gap: '3px',
              mt: 'auto',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              position: 'sticky',
              bottom: 0,
            }}
          >
            <Grid
              container
              sx={{
                mt: 1,
                borderTop: 1,
                borderColor: 'divider',
                width: '100%',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-evenly',
                gap: 1,
                backgroundColor: 'background.paper',
                userSelect: 'none',
              }}
            >
              <Grid item sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Tooltip title={isNewsLiked(news) ? 'Unlike' : 'Like'}>
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      return isNewsLiked(news)
                        ? handleUnlikeClick(event, news?._id)
                        : handleLikeClick(event, news?._id);
                    }}
                    sx={{
                      transition: 'color 0.3s ease',
                      color: isNewsLiked(news) ? '#0a4fac' : 'grey',
                      mr: 1,
                      userSelect: 'none',
                    }}
                  >
                    {isNewsLiked(news) ? (
                      <ThumbUpSharp sx={{ fontSize: '20px' }} />
                    ) : (
                      <ThumbUpAltOutlined sx={{ fontSize: '20px' }} />
                    )}
                  </IconButton>
                </Tooltip>
                <Typography
                  sx={{
                    cursor: 'default',
                    color: isNewsLiked(news) ? '#0a4fac' : 'grey',
                    fontWeight: 'bold',
                    fontSize: '15px',
                    mt: 0.4,
                    userSelect: 'none',
                  }}
                  onClick={(event) =>
                    isNewsLiked(news) ? handleUnlikeClick(event, news?._id) : handleLikeClick(event, news?._id)
                  }
                >
                  Like
                </Typography>
              </Grid>
              <Divider orientation="vertical" flexItem sx={{ mt: 0.7 }} />
              <Grid
                item
                sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick(news?._id);
                }}
              >
                <IconButton onClick={() => navigate(`/home/announcement/gnn/`)}>
                  <Tooltip title="Comments">
                    <InsertCommentOutlined sx={{ color: 'grey', fontSize: '20px' }} />
                  </Tooltip>
                </IconButton>
                <Typography
                  sx={{
                    cursor: 'default',
                    color: 'grey',
                    fontWeight: 'bold',
                    fontSize: '15px',
                    mt: 0.3,
                    '&:hover': {
                      color: '#73c2fb',
                    },
                    userSelect: 'none',
                  }}
                >
                  Comment
                </Typography>
              </Grid>
            </Grid>
          </Box>
          {/* <Card sx={{ borderTop: 1, borderColor: 'divider', height: 10, background: '#F4F2EE', borderRadius: 0 }}></Card> */}
        </Card>
      </div>
    );
  }
);

export default NewsRow;
